import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ContentDocument, ContentViewModel } from "../API/Models";
import type { SiteSettings as SiteSettingsModel, SiteSettingsPayload } from "../types/layout";
import { client } from "../Clients";
import DeploymentStatusPanel from "../Components/DeploymentStatusPanel";
import FooterPreview from "../Components/Preview/FooterPreview";
import { buildFooterData, buildLanguageOptions } from "../Components/LayoutEditor/previewUtils";
import { buildLayoutPayloadFromSettings, sanitizeLayoutSettings } from "../Components/LayoutEditor/utils";
import { usePageHeader } from "../hooks/usePageHeader";
import { Modal } from "../Components/Modal";
import { FiUpload, FiEye } from "react-icons/fi";
import LayoutPreviewModal from "../Components/LayoutEditor/LayoutPreviewModal";
import { showSuccessModal } from "../Helper/showSuccessModal";
import ContentHistoryPanel from "../Components/ContentHistory/ContentHistoryPanel";

type ContactContent = {
	address: string;
	phone: string;
	email: string;
};

type DeploymentStatus = {
	state: "up-to-date" | "needs-deploy" | "pending";
	deployedAt: string | null;
};

const CONTENT_TYPE_KEY = "contact-info";
const CONTENT_LANGUAGE = "default";
const FALLBACK_LAYOUT_LANGUAGE = "fr";

const normalizeSegment = (value?: string | null) => (value ?? "").trim();

const sanitizeContent = (content?: ContactContent | null): ContactContent => ({
	address: normalizeSegment(content?.address),
	phone: normalizeSegment(content?.phone),
	email: normalizeSegment(content?.email)
});

const areComparableEqual = (left: ContactContent | null, right: ContactContent | null) => {
	if (!left || !right) return false;
	return left.address === right.address && left.phone === right.phone && left.email === right.email;
};

/**
 * Contact-info singleton editor : the address / phone / email shown in the public site footer,
 * stored as one document under the "default" language. Loads GET /api/content/contact-info/default,
 * saves via the standard content PUT; the preview environment then publishes itself. Production
 * still goes through POST /api/deployment/contact-info/default/deploy-production, which promotes
 * the last PREVIEW payload : not the working copy : which is why the production button stays
 * disabled until a preview payload exists. The deployment cards derive up-to-date / needs-deploy
 * by comparing sanitized snapshots (current vs preview vs production) instead of timestamps.
 * Fields are read-only until "Enable editing"; a footer preview and per-document history sit
 * alongside the form.
 */
const ContactInfo: React.FC = () => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deployingProduction, setDeployingProduction] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [content, setContent] = useState<ContactContent>(sanitizeContent());
	const [documentId, setDocumentId] = useState<string | null>(null);
	const [previewContent, setPreviewContent] = useState<ContactContent | null>(null);
	const [productionContent, setProductionContent] = useState<ContactContent | null>(null);
	const [previewDeployedAt, setPreviewDeployedAt] = useState<string | null>(null);
	const [productionDeployedAt, setProductionDeployedAt] = useState<string | null>(null);
	const [layoutLocale, setLayoutLocale] = useState<string>(FALLBACK_LAYOUT_LANGUAGE);
	const [isEditing, setIsEditing] = useState(false);
	const [uploadModalOpen, setUploadModalOpen] = useState(false);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [uploadPreview, setUploadPreview] = useState<string | null>(null);
	const [uploadParsed, setUploadParsed] = useState<ContactContent | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [previewOpen, setPreviewOpen] = useState(false);

	const currentComparable = useMemo(() => sanitizeContent(content), [content]);
	const previewComparable = useMemo(() => (previewContent ? sanitizeContent(previewContent) : null), [previewContent]);
	const productionComparable = useMemo(
		() => (productionContent ? sanitizeContent(productionContent) : null),
		[productionContent]
	);

	const previewStatus = useMemo<DeploymentStatus>(() => {
		if (!previewComparable) return { state: "pending", deployedAt: previewDeployedAt };
		if (!currentComparable) return { state: "pending", deployedAt: previewDeployedAt };
		return {
			state: areComparableEqual(previewComparable, currentComparable) ? "up-to-date" : "needs-deploy",
			deployedAt: previewDeployedAt
		};
	}, [previewComparable, currentComparable, previewDeployedAt]);

	const productionStatus = useMemo<DeploymentStatus>(() => {
		if (!productionComparable) return { state: "pending", deployedAt: productionDeployedAt };
		if (!previewComparable) return { state: "needs-deploy", deployedAt: productionDeployedAt };
		return {
			state: areComparableEqual(productionComparable, previewComparable) ? "up-to-date" : "needs-deploy",
			deployedAt: productionDeployedAt
		};
	}, [productionComparable, previewComparable, productionDeployedAt]);

	// Layout preview fetching lives inside the shared preview components and modals.

	const applyViewModel = useCallback((view?: ContentViewModel | null) => {
		const current = sanitizeContent((view?.current?.data as ContactContent) ?? undefined);
		const preview = sanitizeContent((view?.preview?.payload as ContactContent) ?? undefined);
		const production = sanitizeContent((view?.production?.payload as ContactContent) ?? undefined);

		setContent(current);
		setDocumentId(view?.current?.id ?? null);
		setPreviewContent(preview.address || preview.phone || preview.email ? preview : null);
		setProductionContent(production.address || production.phone || production.email ? production : null);
		setPreviewDeployedAt(view?.preview?.deployedAt ?? null);
		setProductionDeployedAt(view?.production?.deployedAt ?? null);
		setIsEditing(false);
	}, []);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		setSuccess(null);
		try {
			const response = await client.content.apiContentSchemaKeyLanguageGet(CONTENT_TYPE_KEY, CONTENT_LANGUAGE);
			applyViewModel(response.data ?? null);
		} catch (err) {
			console.error(err);
			setError("Unable to load contact information. Please try again.");
		} finally {
			setLoading(false);
		}
	}, [applyViewModel]);

	useEffect(() => {
		load();
	}, [load]);

	const handleSave = useCallback(async () => {
		setSaving(true);
		setError(null);
		setSuccess(null);
		try {
			const payload: ContentDocument = {
				contentType: CONTENT_TYPE_KEY,
				language: CONTENT_LANGUAGE,
				data: sanitizeContent(content)
			};
			// slug query parameter is only required for page schema; contact-info does not use it.
			const response = await client.content.apiContentSchemaKeyLanguagePut(
				CONTENT_TYPE_KEY,
				CONTENT_LANGUAGE,
				undefined,
				payload
			);
			applyViewModel(response.data ?? null);
			const successMessage = "Contact information saved successfully.";
			setSuccess(successMessage);
			// L'identifiant est lu sur la reponse : l'etat React n'est pas encore a jour ici.
			const savedId = response.data?.current?.id ?? null;
			showSuccessModal({
				title: "Contact info saved",
				message: successMessage,
				// Pour qui detient le droit : enregistrer et mettre en production en un geste.
				publish: savedId
					? { contentId: savedId, contentType: CONTENT_TYPE_KEY, onPublished: () => void load() }
					: undefined
			});
		} catch (err) {
			console.error(err);
			setError("Unable to save contact information. Please review your changes and try again.");
		} finally {
			setSaving(false);
		}
	}, [content, client, applyViewModel, load]);

	const handleDeployProduction = async () => {
		setDeployingProduction(true);
		setError(null);
		setSuccess(null);
		try {
			await client.deployment.apiDeploymentSchemaKeyLanguageDeployProductionPost(CONTENT_TYPE_KEY, CONTENT_LANGUAGE);
			const successMessage = "Contact information promoted to production.";
			setSuccess(successMessage);
			showSuccessModal({
				title: "Production deploy complete",
				message: successMessage
			});
			await load();
		} catch (err) {
			console.error(err);
			setError("Unable to deploy to production. Please deploy to preview first, then try again.");
		} finally {
			setDeployingProduction(false);
		}
	};

	const openUploadModal = useCallback(() => {
		setUploadModalOpen(true);
		setUploadFile(null);
		setUploadPreview(null);
		setUploadParsed(null);
		setUploadError(null);
	}, []);

	const closeUploadModal = useCallback(() => {
		setUploadModalOpen(false);
		setUploadFile(null);
		setUploadPreview(null);
		setUploadParsed(null);
		setUploadError(null);
	}, []);

	const handleJsonSelect = useCallback((file: File | null) => {
		setUploadFile(file);
		setUploadError(null);
		setUploadPreview(null);
		setUploadParsed(null);
		if (!file) return;
		const reader = new FileReader();
		reader.onload = () => {
			try {
				const text = typeof reader.result === "string" ? reader.result : "";
				setUploadPreview(text);
				const parsed = JSON.parse(text) as ContactContent;
				setUploadParsed(sanitizeContent(parsed));
			} catch (err) {
				console.error(err);
				setUploadError("Invalid JSON file. Please verify structure.");
			}
		};
		reader.onerror = () => {
			setUploadError("Unable to read the selected file.");
		};
		reader.readAsText(file);
	}, []);

	const handleApplyUploadedJson = useCallback(() => {
		if (!uploadParsed) return;
		setContent(sanitizeContent(uploadParsed));
		setSuccess("Contact JSON imported. Review and save.");
		setError(null);
		closeUploadModal();
	}, [uploadParsed, closeUploadModal]);

	const headerActions = useMemo(
		() => (
			<div className="flex flex-wrap gap-2">
				<button
					type="button"
					onClick={() => setPreviewOpen(true)}
					disabled={loading}
					className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					<FiEye className="inline-block mr-2" /> Preview
				</button>
				<button
					type="button"
					onClick={openUploadModal}
					className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					<FiUpload className="inline-block mr-2" /> Upload JSON
				</button>
				{isEditing ? (
					<>
						<button
							type="button"
							onClick={handleSave}
							disabled={saving}
							className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
						>
							{saving ? "Saving…" : "Save"}
						</button>
						<button
							type="button"
							onClick={() => {
								setIsEditing(false);
								load();
							}}
							className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
						>
							Cancel
						</button>
					</>
				) : (
					<button
						type="button"
						onClick={() => setIsEditing(true)}
						className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
					>
						Enable editing
					</button>
				)}
			</div>
		),
		[loading, isEditing, saving, handleSave, load, openUploadModal]
	);

	usePageHeader({
		title: "Contact information",
		actions: headerActions
	});

	const deploymentCards = [
		{
			label: "Preview environment",
			status: previewStatus.state,
			deployedAt: previewStatus.deployedAt,
			action: (
				<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400" title="La preview se met à jour automatiquement après chaque enregistrement">
					Mise à jour automatique
				</span>
			)
		},
		{
			label: "Production environment",
			status: productionStatus.state,
			deployedAt: productionStatus.deployedAt,
			action: (
				<button
					type="button"
					onClick={handleDeployProduction}
					disabled={deployingProduction || saving || !previewComparable}
					className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-4 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{deployingProduction ? "Promoting…" : "Promote to production"}
				</button>
			)
		}
	];

	return (
		<>
			<div className="w-full h-full">
				<div className="px-4 sm:px-6 pt-4 pb-6 space-y-5">
					<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
						<p className="text-sm text-slate-600">
							Manage contact information deployed to preview/production. Only the latest preview payload can be promoted.
						</p>
					</div>
					{error && (
						<div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
							{error}
						</div>
					)}
					{success && (
						<div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
							{success}
						</div>
					)}

					<DeploymentStatusPanel cards={deploymentCards} />

					{loading ? (
						<div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center text-slate-500">
							Loading contact information…
						</div>
					) : (
						<div className="grid layout-editor-grid gap-6 xl:gap-8">
							<div className="space-y-6">
								<div className="grid grid-cols-1 gap-4">
									<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
										<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Address</label>
										<textarea
											className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
											value={content.address}
											disabled={!isEditing}
											onChange={(event) => setContent((prev) => sanitizeContent({ ...prev, address: event.target.value }))}
											rows={3}
										/>
									</div>
									<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
										<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</label>
										<input
											type="text"
											className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
											value={content.phone}
											disabled={!isEditing}
											onChange={(event) => setContent((prev) => sanitizeContent({ ...prev, phone: event.target.value }))}
										/>
									</div>
									<div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm space-y-3">
										<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</label>
										<input
											type="email"
											className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-400 focus:outline-none disabled:bg-slate-100"
											value={content.email}
											disabled={!isEditing}
											onChange={(event) => setContent((prev) => sanitizeContent({ ...prev, email: event.target.value }))}
										/>
									</div>
								</div>
							</div>

							<aside className="w-full">
								<FooterPreview
									initialLocale={layoutLocale}
									className="sticky top-4 max-h-[calc(100vh-3rem)] space-y-3 overflow-y-auto pr-1"
								/>
							</aside>
						</div>
					)}
					{!loading && <ContentHistoryPanel contentId={documentId} />}
				</div>
			</div>

			<Modal show={uploadModalOpen} onHide={closeUploadModal} centered size="lg">
				<Modal.Header closeButton>
					<Modal.Title>Import contact JSON</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="flex flex-col gap-6 lg:flex-row">
						<div className="min-w-[320px] max-w-[360px] space-y-4">
							<div>
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Upload</p>
								<h2 className="text-xl font-semibold text-slate-900">Select a contact payload</h2>
								<p className="mt-2 text-sm text-slate-500">JSON must follow the contact-info schema.</p>
							</div>
							<label className="text-xs font-semibold uppercase tracking-wide text-slate-500">File</label>
							<input
								type="file"
								accept=".json,application/json"
								className="mt-1 block w-full cursor-pointer text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-slate-800"
								onChange={(event) => handleJsonSelect(event.target.files ? event.target.files[0] : null)}
							/>
							{uploadFile && <p className="text-xs text-slate-500">Selected: {uploadFile.name}</p>}
							{uploadError && (
								<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{uploadError}</p>
							)}
							{uploadParsed && !uploadError && (
								<p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
									File parsed successfully. Review and apply.
								</p>
							)}
							<p className="text-xs text-slate-400">Accepted format: .json (UTF-8)</p>
						</div>
						<div className="flex-1 max-h-[70vh] rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
							{uploadPreview ? (
								<pre className="h-full w-full overflow-auto rounded-xl bg-white p-4 text-xs text-slate-800">{uploadPreview}</pre>
							) : (
								<div className="flex h-full min-h-[320px] flex-col items-center justify-center text-center text-sm text-slate-500">
									<p className="text-base font-semibold text-slate-700">No preview yet</p>
									<p className="mt-2 text-xs text-slate-500">Choose a JSON file to show its content here.</p>
								</div>
							)}
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={handleApplyUploadedJson}
						disabled={!uploadParsed}
						className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						Apply JSON
					</button>
					<button
						type="button"
						onClick={closeUploadModal}
						className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
					>
						Close
					</button>
				</Modal.Footer>
			</Modal>
			<LayoutPreviewModal
				show={previewOpen}
				onClose={() => setPreviewOpen(false)}
				initialLocale={layoutLocale}
			/>
		</>
	);
};

export default ContactInfo;
